import moment from "moment";
import { capitalize } from "@/utils/string";

/**
 * Construye la etiqueta de período a partir de un rango de índices del array
 * `monthHeaders` (cada header tiene formato "MMM YYYY", ej: "Abr 2025").
 *
 * Salidas posibles:
 *  - Mismo mes y año:        "Abr 2025"
 *  - Mismo año, distinto mes: "Ene - Abr 2025"
 *  - Cruza año:               "May 2024 - Abr 2025"
 */
export function formatPeriodLabel(monthHeaders, indices) {
    if (!indices || !indices.length) return "-";

    const firstStr = monthHeaders[indices[0]];
    const lastStr = monthHeaders[indices[indices.length - 1]];

    const firstMonth = firstStr.split(" ")[0];
    const firstYear = firstStr.split(" ").pop();
    const lastMonth = lastStr.split(" ")[0];
    const lastYear = lastStr.split(" ").pop();

    const capFirst = capitalize(firstMonth);
    const capLast = capitalize(lastMonth);

    if (firstMonth === lastMonth && firstYear === lastYear) {
        return `${capFirst} ${firstYear}`;
    }
    if (firstYear === lastYear) {
        return `${capFirst} - ${capLast} ${lastYear}`;
    }
    return `${capFirst} ${firstYear} - ${capLast} ${lastYear}`;
}

// ----- Helpers para summary cards (rango definido por fechas ISO) -----

/**
 * Construye la etiqueta de período a partir de dos fechas ISO (YYYY-MM-DD).
 * Variante de `formatPeriodLabel` que no depende de `monthHeaders`.
 */
export function formatRangeLabel(startIso, endIso) {
    const start = moment(startIso);
    const end = moment(endIso);
    const startMonth = capitalize(start.format("MMM").replace(".", ""));
    const startYear = start.format("YYYY");
    const endMonth = capitalize(end.format("MMM").replace(".", ""));
    const endYear = end.format("YYYY");

    if (startMonth === endMonth && startYear === endYear) {
        return `${startMonth} ${startYear}`;
    }
    if (startYear === endYear) {
        return `${startMonth} - ${endMonth} ${endYear}`;
    }
    return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
}

/**
 * Convierte una fecha ISO al formato usado por monthHeaders ("MMM YYYY", ej "abr 2025").
 */
export function dateToHeaderLabel(iso) {
    return moment(iso).format("MMM YYYY").replace(".", "");
}

/**
 * Cantidad de meses incluidos entre dos fechas ISO (ambos extremos inclusive).
 */
export function monthsBetween(startIso, endIso) {
    const start = moment(startIso).startOf("month");
    const end = moment(endIso).startOf("month");
    return end.diff(start, "months") + 1;
}

/**
 * Devuelve los índices de monthHeaders correspondientes al rango [startIso, endIso].
 * Retorna [] si alguno de los extremos no está presente.
 */
export function indicesForRange(monthHeaders, startIso, endIso) {
    const startIdx = monthHeaders.indexOf(dateToHeaderLabel(startIso));
    const endIdx = monthHeaders.indexOf(dateToHeaderLabel(endIso));
    if (startIdx === -1 || endIdx === -1) return [];
    const indices = [];
    for (let i = startIdx; i <= endIdx; i++) indices.push(i);
    return indices;
}

/**
 * Formatea un ratio (ej. 0.0856) como porcentaje con 1 decimal y redondeo
 * common (half-up): 8.56% → "8.6%". Devuelve "" si ratio es null/undefined.
 */
export function formatPercent(ratio) {
    if (ratio === null || ratio === undefined) return "";
    const pct = Math.round(ratio * 1000) / 10;
    return `${pct.toFixed(1)}%`;
}
